import { computed, onBeforeUnmount, onMounted, type Ref, type WritableComputedRef } from 'vue';

import type { Nullable } from '@nova/core';
import { useDialogListener, type CirceCustomEventMap } from '@/composables/useEventBasedDialog';

export type PanelIntersectArg = { isIntersecting?: boolean } | boolean;
export type PanelOnIntersect = (entry?: PanelIntersectArg) => void;

export interface CheckTaskPanelControls {
  isOpen: WritableComputedRef<boolean>;
  closePanel: () => void;
}

export function useCheckTaskPanel<K extends keyof CirceCustomEventMap>(dialogName: K) {
  const {
    isDialogOpen,
    dialogProps,
    closeDialog,
    resetDialogProps,
    subscribeToDialogEvent,
    unsubscribeFromDialogEvent
  } = useDialogListener(dialogName);

  onMounted(() => {
    subscribeToDialogEvent();
  });

  onBeforeUnmount(() => {
    unsubscribeFromDialogEvent();
  });

  function handleAfterLeave(): void {
    resetDialogProps();
  }

  function buildControls(onIntersect: PanelOnIntersect): CheckTaskPanelControls {
    function closePanel(): void {
      onIntersect(false);
      closeDialog();
    }

    const isOpen = computed<boolean>({
      get: () => isDialogOpen.value,
      set: (nextIsDialogOpen: boolean) => {
        if (!nextIsDialogOpen) {
          closePanel();
        }
      }
    });

    return { isOpen, closePanel };
  }

  return {
    dialogProps: dialogProps as Ref<Nullable<CirceCustomEventMap[K]['detail']>>,
    handleAfterLeave,
    buildControls
  };
}
